import { Link } from 'react-router-dom';
import { ShieldWarning, ArrowLeft, House } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

function AdminLoading() {
  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center ambient-shadow">
          <span className="h-5 w-5 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
        </div>
        <p className="text-sm text-muted-foreground">Verifying admin access…</p>
      </div>
    </div>
  );
}

function ForbiddenScreen({ user }) {
  const email = user?.email;
  return (
    <div className="min-h-screen bg-background grid place-items-center px-6 py-12">
      <div className="surface-card ambient-shadow-lg max-w-lg w-full p-8 md:p-10 text-center">
        <div className="mx-auto mb-5 grid place-items-center h-14 w-14 rounded-2xl bg-amber-100 text-amber-600">
          <ShieldWarning size={26} weight="fill" />
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-foreground mb-2">
          Admin access required
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
          This area is restricted to platform administrators. Your account
          {email ? <> (<span className="font-medium text-foreground">{email}</span>)</> : null}{' '}
          doesn't have the <span className="font-medium text-foreground">super_admin</span> role.
        </p>

        <div className="mt-6 rounded-xl bg-muted/60 border border-border/70 p-4 text-left">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
            How to grant access
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            In your Supabase project, open the SQL editor and run:
          </p>
          <pre className="mt-2 rounded-lg bg-foreground/[0.06] text-foreground text-[11px] font-mono px-3 py-2 overflow-x-auto leading-relaxed">
{`update public.users
set role = 'super_admin'
where email = '${email || 'your@email.com'}';`}
          </pre>
          <p className="text-[11px] text-muted-foreground mt-2">
            Then sign out and sign back in to refresh your session.
          </p>
        </div>

        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-2">
          <Button asChild className="rounded-xl bg-primary text-primary-foreground hover:opacity-95 ambient-shadow gap-2 px-5 h-11">
            <Link to="/dashboard" data-testid="admin-forbidden-back">
              <House size={16} weight="bold" />
              Go to dashboard
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl gap-2 px-5 h-11 border-border">
            <Link to="/" data-testid="admin-forbidden-home">
              <ArrowLeft size={16} weight="bold" />
              Back to home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminGuard({ children }) {
  const { user, loading } = useAuth();

  if (loading) return <AdminLoading />;

  if (!user || user.role !== 'super_admin') {
    return <ForbiddenScreen user={user} />;
  }

  return children;
}
